import { brandApi } from "@/lib/axios";

/**
 * Ad Performance: Meta's own figures (primary) beside Reveshare-attributed
 * sales (secondary), per campaign, ad set, ad and creator. Shapes mirror the
 * backend views in meta-performance.views.ts.
 */

export type PeriodDays = 7 | 30 | 90;

export interface PeriodView {
  days: number;
  since: string;
  until: string;
  previousSince: string;
  previousUntil: string;
}

export interface CurrencyView {
  account: string;
  store: string | null;
  conflict: boolean;
}

export interface Money {
  amount: number;
  currency: string;
}

export type MoneyOrConflict =
  | { conflict: false; amount: number; currency: string }
  | { conflict: true; byCurrency: Money[] };

export interface CrossFigure {
  value: number | null;
  reason: "CURRENCY_CONFLICT" | "NO_SPEND" | "NO_DATA" | null;
}

export interface MetaDerived {
  spend: number;
  impressions: number;
  reach: number;
  clicks: number;
  linkClicks: number;
  purchases: number;
  purchaseValue: number;
  video3s: number;
  thruplay: number;
  p25: number;
  p50: number;
  p75: number;
  p100: number;
  rows: number;
  ctr: number | null;
  linkCtr: number | null;
  cpc: number | null;
  cpm: number | null;
  cpa: number | null;
  roas: number | null;
  frequency: number | null;
  conversionRate: number | null;
  hookRate: number | null;
  thruplayRate: number | null;
}

export type MetaDelta = Partial<Record<keyof MetaDerived, number | null>>;

export interface DailyPoint {
  date: string;
  spend: number;
  purchaseValue: number;
  purchases: number;
  clicks: number;
  impressions: number;
}

export interface VideoFunnelStep {
  key: string;
  label: string;
  value: number;
  ofImpressions: number | null;
}

export type RoasBucket = "SCALE" | "OPTIMIZE" | "CUT" | "UNRATED";

export interface ReveshareView {
  orders: number;
  revenue: MoneyOrConflict;
  commission: MoneyOrConflict;
  netRoas: CrossFigure;
}

export type RuleAction = "NOTIFY" | "PAUSE_AND_NOTIFY";

export interface RuleView {
  id: string;
  metaCampaignId: string;
  roasFloor: number | null;
  roasCeiling: number | null;
  ctrFloor: number | null;
  ctrCeiling: number | null;
  windowDays: number;
  minSpend: number;
  action: RuleAction;
  enabled: boolean;
  openEvents: number;
}

export type RuleMetric = "ROAS_FLOOR" | "ROAS_CEILING" | "CTR_FLOOR" | "CTR_CEILING";
export type RuleOutcome = "NOTIFIED" | "PAUSED" | "PAUSE_FAILED";

export interface RuleEventView {
  id: string;
  metaCampaignId: string;
  metaAdsetId: string | null;
  metaAdId: string;
  adName: string | null;
  metric: RuleMetric;
  observed: number;
  threshold: number;
  outcome: RuleOutcome;
  error: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

export interface LaunchedView {
  adRecordId: string;
  program: { id: string; title: string };
  content: {
    id: string;
    title: string | null;
    mediaType: string | null;
    thumbnailUrl: string | null;
    mediaUrl: string | null;
  };
  creator: {
    id: string;
    firstName: string;
    lastName: string;
    username: string | null;
    profileImage: string | null;
  } | null;
  adRequest: {
    id: string;
    status: string;
    flatFee: number | null;
    commissionRate: number | null;
  } | null;
  pausedByReveshareAt: string | null;
}

export type InsightLevel = "account" | "campaign" | "adset" | "ad";

export interface ObjectView {
  level: InsightLevel;
  id: string;
  name: string;
  status: string | null;
  effectiveStatus: string | null;
  objective: string | null;
  campaignId: string | null;
  adsetId: string | null;
  startTime: string | null;
  createdTime: string | null;
  dailyBudget: Money | null;
  meta: MetaDerived;
  bucket: RoasBucket;
  reveshare: ReveshareView;
  reveshareLaunched: boolean;
  launched: LaunchedView | null;
  spendShare: number | null;
  rule: RuleView | null;
  openEvents: number;
}

export interface SummaryView {
  period: PeriodView;
  currency: CurrencyView;
  current: MetaDerived;
  previous: MetaDerived;
  delta: MetaDelta;
  series: DailyPoint[];
  reveshare: ReveshareView;
  launchedAds: number;
  attention: RuleEventView[];
}

export interface CampaignsView {
  period: PeriodView;
  currency: CurrencyView;
  totalSpend: number;
  campaigns: ObjectView[];
}

export interface CampaignDetailView {
  period: PeriodView;
  currency: CurrencyView;
  campaign: ObjectView;
  series: DailyPoint[];
  adsets: ObjectView[];
}

export interface AdsetDetailView {
  period: PeriodView;
  currency: CurrencyView;
  adset: ObjectView;
  series: DailyPoint[];
  ads: ObjectView[];
}

export type Breakdown = "placement" | "country" | "age_gender";

export interface AdDetailView {
  period: PeriodView;
  currency: CurrencyView;
  ad: ObjectView;
  previous: MetaDerived;
  delta: MetaDelta;
  series: DailyPoint[];
  funnel: VideoFunnelStep[];
  creatorCost: MoneyOrConflict | null;
  trueRoas: CrossFigure | null;
  breakdown: { kind: Breakdown; rows: { label: string; meta: MetaDerived }[] } | null;
  events: RuleEventView[];
}

export interface CreatorView {
  affiliateId: string;
  score: number | null;
  rank: number | null;
  revenue: MoneyOrConflict;
  orders: number;
  activeDays: number;
  weeksWithSales: number;
  weeksInWindow: number;
  shrunkRevenuePerDay: number | null;
  consistency: number | null;
  weekly: number[];
  unscoredReason: "NO_ACTIVE_DAYS" | "CURRENCY_CONFLICT" | null;
  creator: NonNullable<LaunchedView["creator"]>;
  adsCount: number;
  meta: MetaDerived;
  commission: MoneyOrConflict;
  feesPaid: MoneyOrConflict;
  netRoas: CrossFigure;
  trueRoas: CrossFigure;
  bestAd: { metaAdId: string; name: string; thumbnailUrl: string | null; revenue: number } | null;
}

export interface CreatorsView {
  period: PeriodView;
  currency: CurrencyView;
  creators: CreatorView[];
}

export interface CreatorDetailView {
  period: PeriodView;
  currency: CurrencyView;
  creator: CreatorView;
  ads: ObjectView[];
}

export interface UpsertRuleInput {
  roasFloor?: number | null;
  roasCeiling?: number | null;
  ctrFloor?: number | null;
  ctrCeiling?: number | null;
  windowDays?: number;
  minSpend?: number;
  action?: RuleAction;
  enabled?: boolean;
}

const BASE = "/meta-ads/performance";

function scoped(days: number, reveshareOnly?: boolean) {
  return { days, ...(reveshareOnly ? { reveshareOnly: "true" } : {}) };
}

export async function fetchPerformanceSummary(days: number, reveshareOnly: boolean): Promise<SummaryView> {
  const { data } = await brandApi.get<SummaryView>(`${BASE}/summary`, { params: scoped(days, reveshareOnly) });
  return data;
}

export async function refreshPerformance(days: number): Promise<SummaryView> {
  const { data } = await brandApi.post<SummaryView>(`${BASE}/refresh`, null, { params: { days } });
  return data;
}

export async function fetchPerformanceCampaigns(days: number, reveshareOnly: boolean): Promise<CampaignsView> {
  const { data } = await brandApi.get<CampaignsView>(`${BASE}/campaigns`, { params: scoped(days, reveshareOnly) });
  return data;
}

export async function fetchPerformanceCampaign(campaignId: string, days: number): Promise<CampaignDetailView> {
  const { data } = await brandApi.get<CampaignDetailView>(`${BASE}/campaigns/${campaignId}`, { params: { days } });
  return data;
}

export async function fetchPerformanceAdset(adsetId: string, days: number): Promise<AdsetDetailView> {
  const { data } = await brandApi.get<AdsetDetailView>(`${BASE}/adsets/${adsetId}`, { params: { days } });
  return data;
}

export async function fetchPerformanceAd(adId: string, days: number, breakdown?: Breakdown | null): Promise<AdDetailView> {
  const { data } = await brandApi.get<AdDetailView>(`${BASE}/ads/${adId}`, {
    params: { days, ...(breakdown ? { breakdown } : {}) },
  });
  return data;
}

export async function resumePerformanceAd(adId: string): Promise<{ resumed: true }> {
  const { data } = await brandApi.post<{ resumed: true }>(`${BASE}/ads/${adId}/resume`);
  return data;
}

export async function fetchPerformanceCreators(days: number): Promise<CreatorsView> {
  const { data } = await brandApi.get<CreatorsView>(`${BASE}/creators`, { params: { days } });
  return data;
}

export async function fetchPerformanceCreator(affiliateId: string, days: number): Promise<CreatorDetailView> {
  const { data } = await brandApi.get<CreatorDetailView>(`${BASE}/creators/${affiliateId}`, { params: { days } });
  return data;
}

export async function fetchPerformanceRules(): Promise<RuleView[]> {
  const { data } = await brandApi.get<RuleView[]>(`${BASE}/rules`);
  return data;
}

export async function upsertPerformanceRule(campaignId: string, input: UpsertRuleInput): Promise<RuleView> {
  const { data } = await brandApi.put<RuleView>(`${BASE}/campaigns/${campaignId}/rule`, input);
  return data;
}

export async function removePerformanceRule(campaignId: string): Promise<{ removed: true }> {
  const { data } = await brandApi.delete<{ removed: true }>(`${BASE}/campaigns/${campaignId}/rule`);
  return data;
}

export async function fetchPerformanceEvents(params: { page?: number; limit?: number; open?: boolean }) {
  const { data } = await brandApi.get<{ events: RuleEventView[]; total: number; page: number; limit: number }>(
    `${BASE}/events`,
    { params: { page: params.page ?? 1, limit: params.limit ?? 20, ...(params.open ? { open: "true" } : {}) } },
  );
  return data;
}
